import { useCallback, useEffect, useRef, useState } from "react";
import {
  createTemplateStudioConversation,
  resumeTemplateStudioConversation,
  sendTemplateStudioConversationTurn,
  type TemplateStudioConversation,
  type TemplateStudioConversationCommand,
  TemplateStudioConversationError,
  type TemplateStudioConversationScope,
} from "@/lib/template-studio/templateStudioConversations";
import useAuth, { type AuthState } from "@/store/auth.slice";

/** The hook fills in `requestId` and `expectedRevision`. */
type StudioConversationCommand =
  TemplateStudioConversationCommand extends infer C
    ? C extends unknown
      ? Omit<C, "requestId" | "expectedRevision">
      : never
    : never;

/** Starting point stored when the first turn creates the conversation. */
interface StudioConversationSeed {
  initialDraft: Record<string, unknown> | null;
  previous?: unknown;
  componentTheme?: Record<string, unknown>;
}

const errorMessage = (error: unknown) =>
  error instanceof Error && error.message
    ? error.message
    : "Could not load the conversation.";

/**
 * The stored conversation for one Studio scope. Only its id is kept in the
 * browser, per tenant, user, and scope; history lives in layout-studio.
 * A conversation is created by its first turn, so opening Studio without
 * sending anything stores nothing. Applying turn responses is up to the caller.
 */
export const useStudioConversation = ({
  enabled,
  scope,
  onResume,
}: {
  enabled: boolean;
  scope: TemplateStudioConversationScope;
  /** Called with each conversation `resume` loads, before `resume` resolves. */
  onResume: (conversation: TemplateStudioConversation) => Promise<void>;
}) => {
  const userId = useAuth((state: AuthState) => state.user.id);
  const tenantId = useAuth((state: AuthState) => state.user.default_tenant.id);
  const storageKey = `template-studio-conversation:${tenantId}:${userId}:${scope}`;
  const [conversation, setConversation] =
    useState<TemplateStudioConversation | null>(null);
  const [isLoading, setIsLoading] = useState(
    () => enabled && localStorage.getItem(storageKey) != null,
  );
  const [error, setError] = useState<string | null>(null);
  const conversationRef = useRef<TemplateStudioConversation | null>(null);
  // Bumped by startNew and by a new resume, so their stale results are dropped.
  const generationRef = useRef(0);
  const onResumeRef = useRef(onResume);
  onResumeRef.current = onResume;

  const commit = useCallback(
    (generation: number, next: TemplateStudioConversation | null) => {
      if (generation !== generationRef.current) return false;
      conversationRef.current = next;
      setConversation(next);
      return true;
    },
    [],
  );

  /** Loads the stored conversation; resolves to it, or `null` if there is none or it failed. */
  const resume = useCallback(async () => {
    const generation = ++generationRef.current;
    const conversationId =
      conversationRef.current?.conversationId ??
      localStorage.getItem(storageKey);
    if (conversationId == null) {
      setIsLoading(false);
      return null;
    }
    setIsLoading(true);
    setError(null);
    try {
      const loaded = await resumeTemplateStudioConversation(
        conversationId,
        scope,
      );
      if (!commit(generation, loaded)) return null;
      await onResumeRef.current(loaded);
      return loaded;
    } catch (failure) {
      if (generation !== generationRef.current) return null;
      // Deleted, expired, or another user's: start fresh on the next turn.
      if (
        failure instanceof TemplateStudioConversationError &&
        failure.status === 404
      ) {
        localStorage.removeItem(storageKey);
        commit(generation, null);
      } else {
        setError(errorMessage(failure));
      }
      return null;
    } finally {
      if (generation === generationRef.current) setIsLoading(false);
    }
  }, [commit, scope, storageKey]);

  useEffect(() => {
    if (!enabled) return;
    void resume();
    return () => {
      generationRef.current++;
    };
  }, [enabled, resume]);

  /**
   * Runs one turn, creating the conversation from `seed` first if there is
   * none. Throws on failure; call `resume` afterwards to pick up the stored
   * revision, since an aborted turn still finishes on the server.
   */
  const send = useCallback(
    async (
      command: StudioConversationCommand,
      { seed, signal }: { seed: StudioConversationSeed; signal?: AbortSignal },
    ): Promise<TemplateStudioConversation> => {
      const generation = generationRef.current;
      let current = conversationRef.current;
      // A stored conversation that hasn't loaded, such as after a failed
      // resume: creating another would orphan its history.
      if (current == null && localStorage.getItem(storageKey) != null) {
        throw new TemplateStudioConversationError(
          409,
          "Your conversation hasn't loaded yet. Try again.",
          "NOT_LOADED",
        );
      }
      if (current == null) {
        current = await createTemplateStudioConversation({
          conversationId: crypto.randomUUID(),
          scope,
          ...seed,
        });
        if (commit(generation, current)) {
          localStorage.setItem(storageKey, current.conversationId);
        }
      }
      const next = await sendTemplateStudioConversationTurn(
        current.conversationId,
        scope,
        {
          ...command,
          requestId: crypto.randomUUID(),
          expectedRevision: current.revision,
        },
        signal,
      );
      commit(generation, next);
      return next;
    },
    [commit, scope, storageKey],
  );

  /** Forgets the current conversation; the next turn starts a new one. */
  const startNew = useCallback(() => {
    generationRef.current++;
    localStorage.removeItem(storageKey);
    conversationRef.current = null;
    setConversation(null);
    setIsLoading(false);
    setError(null);
  }, [storageKey]);

  return { conversation, isLoading, error, send, resume, startNew };
};
